import React from 'react';
import {
    Box,
    Stack,
    Typography,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    CircularProgress,
    Divider,
    Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTheme} from '@mui/material/styles';
import {InputField, SnackbarField} from '@/components/common';
import {createRole, updateRole, getRoleById, type Role} from '@/services/manageService/roleService';
import {usePageCrud} from '@/hooks/usePageCrud';

const ROLE_TYPE_MAP: Record<number, string> = {
    0: 'Super Admin',
    1: 'Admin Role',
    2: 'Accounting Role',
    3: 'Audit Role',
};

const DATA_ACCESS_MAP: Record<number, string> = {
    1: 'Owner',
    2: 'Owner And Subordinates',
    3: 'Dept',
    4: 'Dept And Dept Subordinates',
    5: 'All',
};

interface RoleDetailProps {
    open: boolean;
    roleId?: number;
    onClose: (data?: any) => void;
    isAdd?: boolean;
}

interface RoleForm {
    roleId?: number;
    roleName: string;
    roleType: number;
    remark: string;
    status: number;
    dataType: number;
    isHidden: number;
}

const initialForm: RoleForm = {
    roleName: '',
    roleType: -1,
    remark: '',
    status: 1,
    dataType: 5,
    isHidden: 0,
};

const RoleDetail: React.FC<RoleDetailProps> = ({open, roleId, onClose, isAdd}) => {
    const theme = useTheme();
    const {canCreate, canUpdate} = usePageCrud({
        read: 'admin_role_query',
        create: 'admin:role:create',
        update: 'admin:role:update',
        remove: 'admin:role:delete',
    });
    const [form, setForm] = React.useState<RoleForm>({...initialForm});
    const [loading, setLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    // Superadmin role: display-only (roleType=0 or roleName=superadmin)
    const isSuperadminReadOnly = !isAdd && (
        form.roleType === 0
        || String(form.roleName ?? '').toLowerCase() === 'superadmin'
    );

    React.useEffect(() => {
        if (isAdd && open) {
            setForm({...initialForm});
        } else if (roleId && open) {
            setLoading(true);
            getRoleById(roleId)
                .then((res: any) => {
                    const data: Role = Array.isArray(res) ? res[0] : res;
                    setForm({
                        roleId: data.roleId,
                        roleName: data.roleName ?? '',
                        roleType: Number(data.roleType ?? -1),
                        remark: data.remark ?? '',
                        status: Number(data.status ?? 0),
                        dataType: Number(data.dataType ?? 1),
                        isHidden: Number(data.isHidden ?? 0),
                    });
                })
                .finally(() => setLoading(false));
        }
    }, [roleId, open, isAdd]);

    const validate = () => {
        if (!form.roleName || form.roleName.trim() === '') {
            setSnackbar({open: true, message: 'Role Name is required', severity: 'error'});
            return false;
        }
        return true;
    };

    const handleCreate = async () => {
        if (!validate()) return;
        setSubmitting(true);
        const payload = {...form} as any;
        delete payload.roleId;
        await createRole(payload)
            .then(() =>
                onClose({type: 'create', message: `${form.roleName} Create Successful!`, severity: 'success'}))
            .catch(e =>
                setSnackbar({open: true, message: e?.message || 'Role Create failed!', severity: 'error'}))
            .finally(() => setSubmitting(false));
    };

    const handleSave = async () => {
        if (!validate() || !form.roleId) return;
        setSubmitting(true);
        const payload = {...form}
        await updateRole(form.roleId, payload as any)
            .then(() =>
                onClose({type: 'update', message: 'Update Successful!', severity: 'success'}))
            .catch(e =>
                setSnackbar({open: true, message: e?.message || 'Role Update failed!', severity: 'error'}))
            .finally(() => setSubmitting(false));
    };

    const getRoleTypeLabel = (v: number) => ROLE_TYPE_MAP[v] || String(v);
    const getDataTypeLabel = (v: number) => DATA_ACCESS_MAP[v] || String(v);

    return (
        <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
            <DialogTitle sx={{m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Typography variant="inherit">{isAdd ? 'Add Role' : 'Role Detail'}</Typography>
                <IconButton aria-label="close" onClick={() => onClose()} sx={{color: theme.palette.grey[500]}}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {(loading || submitting) ? (
                    <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                        <CircularProgress color="secondary"/>
                    </Box>
                ) : (
                    <Box component="form" autoComplete="off" sx={{pt: 1}}>
                        <Grid container spacing={2}>
                            {!isAdd && (
                                <Grid size={{xs: 12, sm: 6}}>
                                    <InputField label="Role ID" value={form.roleId ?? ''} fullWidth display/>
                                </Grid>
                            )}
                            <Grid size={{xs: 12}}>
                                <InputField
                                    label="Role Name"
                                    value={form.roleName}
                                    onChange={(e) => setForm({...form, roleName: e.target.value})}
                                    required
                                    fullWidth
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                            <Grid size={{xs: 12, sm: 6}}>
                                <InputField
                                    label="Role Type"
                                    select
                                    size="small"
                                    fullWidth
                                    value={form.roleType}
                                    onChange={(e) => setForm({...form, roleType: Number(e.target.value)})}
                                    display={isSuperadminReadOnly}
                                >
                                    <MenuItem key={-1} value={-1}>Custom Role</MenuItem>
                                    {[0, 1, 2, 3].map((v) => (
                                        <MenuItem key={v} value={v}>{getRoleTypeLabel(v)}</MenuItem>
                                    ))}
                                </InputField>
                            </Grid>
                            <Grid size={{xs: 12, sm: 6}}>
                                <InputField
                                    label="Status"
                                    select
                                    size="small"
                                    fullWidth
                                    value={form.status}
                                    onChange={(e) => setForm({...form, status: Number(e.target.value)})}
                                    display={isSuperadminReadOnly}
                                >
                                    <MenuItem value={0}>Disable</MenuItem>
                                    <MenuItem value={1}>Able</MenuItem>
                                </InputField>
                            </Grid>
                            <Grid size={{xs: 12, sm: 6}}>
                                <InputField
                                    label="Data Access"
                                    select
                                    size="small"
                                    fullWidth
                                    value={form.dataType}
                                    onChange={(e) => setForm({...form, dataType: Number(e.target.value)})}
                                    display={isSuperadminReadOnly}
                                >
                                    {[1, 2, 3, 4, 5].map((v) => (
                                        <MenuItem key={v} value={v}>{getDataTypeLabel(v)}</MenuItem>
                                    ))}
                                </InputField>
                            </Grid>
                            <Grid size={{xs: 12, sm: 6}}>
                                <InputField
                                    label="Hidden"
                                    select
                                    size="small"
                                    fullWidth
                                    value={form.isHidden}
                                    onChange={(e) => setForm({...form, isHidden: Number(e.target.value)})}
                                    display={isSuperadminReadOnly}
                                >
                                    <MenuItem value={0}>Unhidden</MenuItem>
                                    <MenuItem value={1}>Hidden</MenuItem>
                                </InputField>
                            </Grid>
                            <Grid size={{xs: 12}}>
                                <InputField
                                    label="Remark"
                                    value={form.remark}
                                    onChange={(e) => setForm({...form, remark: e.target.value})}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    display={isSuperadminReadOnly}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Stack direction="row" spacing={1} sx={{px: 1, py: 0.5}}>
                    <Button variant="outlined" onClick={() => onClose()}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius }}>Cancel</Button>
                    {isAdd ? (
                        canCreate && (
                            <Button variant="contained" color="secondary" onClick={handleCreate}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius }}>Create</Button>
                        )
                    ) : (
                        !isSuperadminReadOnly && canUpdate && (
                            <Button variant="contained" color="primary" onClick={handleSave}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius }}>Save</Button>
                        )
                    )}
                </Stack>
            </DialogActions>
            <Divider/>
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({...snackbar, open: false})}
            />
        </Dialog>
    );
};

export default RoleDetail;
